import { FinalImpactScoreType, NetScoreFormat } from '@upright/common/model/output/scores/types';
import { allDimensions, allValences } from '@upright/common/model/types';
import { formatNetImpactRatio, formatWithTwoDecimalsGrayNA } from '../../../../../generic/utilities/format';
import { plusIfPositive } from './shared';
import type { ScoreReader as IScoreReader } from '@upright/common/model/output/scores/score-reader';
import { isNil } from 'lodash';

type ScoreReader = Pick<IScoreReader, 'readScore'>;

export function getRelativeValueFormatter() {
  return (value: number) => value.toFixed(1);
}

function getMaximumDimensionValueInScoreReaders(scoreReaders: ScoreReader[]) {
  let maxScore = 0;
  scoreReaders.forEach(scoreReader => {
    allDimensions.forEach(dimension => {
      allValences.forEach(valence => {
        const score = scoreReader.readScore(undefined, dimension, undefined, valence) ?? 0;
        maxScore = Math.max(score, maxScore);
      });
    });
    maxScore = Math.max(scoreReader.readScore(undefined, undefined, undefined, undefined) ?? 0, maxScore);
  });
  return maxScore;
}

export function getAutoScaledAbsoluteValueFormatterFromScoreReaders(scoreReaders: ScoreReader[], moneyMode: boolean) {
  const maxScore = getMaximumDimensionValueInScoreReaders(scoreReaders);
  return getAbsoluteValueFormatter(maxScore, moneyMode);
}

export function getAbsoluteValueFormatter(maxScore: number, moneyMode: boolean, nrOfDigits?: number) {
  if (maxScore >= 2_000_000_000) {
    if (moneyMode) {
      return (value: number) => formatMoneyModeAbsoluteValue('billions', value, nrOfDigits);
    }
    return (value: number) => `${(value / 1_000_000_000).toFixed(1)}G`;
  }

  if (maxScore >= 2_000_000) {
    if (moneyMode) {
      return (value: number) => formatMoneyModeAbsoluteValue('millions', value, nrOfDigits);
    }
    return (value: number) => `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (maxScore >= 2_000) {
    if (moneyMode) {
      return (value: number) => formatMoneyModeAbsoluteValue('thousands', value, nrOfDigits);
    }
    return (value: number) => `${(value / 1_000).toFixed(0)}k`;
  }
  if (maxScore >= 500) {
    if (moneyMode) {
      return (value: number) => formatMoneyModeAbsoluteValue('no_scaling', value, nrOfDigits);
    }
    return (value: number) => `${value.toFixed(0)}`;
  }
  if (moneyMode) {
    return (value: number) => `$${value.toFixed(1)}`;
  }
  return (value: number) => `${value.toFixed(1)}`;
}

const moneyModeScalesDict = {
  billions: { divider: 1_000_000_000, abbreviation: 'bn', nrOfDigits: 1 },
  millions: { divider: 1_000_000, abbreviation: 'm', nrOfDigits: 1 },
  thousands: { divider: 1_000, abbreviation: 'k', nrOfDigits: 0 },
  no_scaling: { divider: 1, abbreviation: '', nrOfDigits: 0 },
} as const;

type Scale = keyof typeof moneyModeScalesDict;

function formatMoneyModeAbsoluteValue(scale: Scale, value: number, nrOfDigits?: number) {
  // Adds a sign only to negative values as impact bars call this formatting function with absolute values
  // and adds a sign based on valence.
  let sign = '';
  if (value < 0) {
    sign = '-';
  }
  const abbreviation = moneyModeScalesDict[scale].abbreviation;
  const divider = moneyModeScalesDict[scale].divider;
  const nrOfDigitsToUse = !isNil(nrOfDigits) ? nrOfDigits : moneyModeScalesDict[scale].nrOfDigits;
  return `${sign}$${Math.abs(value / divider).toFixed(nrOfDigitsToUse)}${abbreviation}`;
}

export type ImpactScoreFormatters = {
  [key in FinalImpactScoreType]: (value: number) => string;
};

export function getImpactScoreFormattersFromScoreReaders(
  scoreReaders: ScoreReader[],
  options?: { hideCurrencyUnits?: boolean },
) {
  const hideCurrencyUnits = options?.hideCurrencyUnits ?? false;
  return {
    [FinalImpactScoreType.ABSOLUTE]: getAutoScaledAbsoluteValueFormatterFromScoreReaders(scoreReaders, false),
    [FinalImpactScoreType.RELATIVE]: getRelativeValueFormatter(),
    [FinalImpactScoreType.CENTS_PER_DOLLAR]: getRelativeValueFormatter(),
    [FinalImpactScoreType.DOLLARS_PER_YEAR]: getAutoScaledAbsoluteValueFormatterFromScoreReaders(
      scoreReaders,
      !hideCurrencyUnits,
    ),
  };
}

// TODO ML: Clean away the boolean here
export function formatImpactScore(
  value: number,
  formatters: ImpactScoreFormatters,
  scoreType: boolean | FinalImpactScoreType,
) {
  switch (scoreType) {
    case FinalImpactScoreType.RELATIVE:
      return formatters[FinalImpactScoreType.RELATIVE](value);
    case FinalImpactScoreType.ABSOLUTE:
      return formatters[FinalImpactScoreType.RELATIVE](value);
    case FinalImpactScoreType.CENTS_PER_DOLLAR:
      return formatters[FinalImpactScoreType.CENTS_PER_DOLLAR](value);
    case FinalImpactScoreType.DOLLARS_PER_YEAR:
      return formatters[FinalImpactScoreType.DOLLARS_PER_YEAR](value);
    case false: // Means relativeMode: false
      return formatters[FinalImpactScoreType.ABSOLUTE](value);
    case true: // Means relativeMode: true
      return formatters[FinalImpactScoreType.RELATIVE](value);
  }
}

export function formatNetScore(
  value: number,
  netScoreFormat: NetScoreFormat,
  formatters: ImpactScoreFormatters,
  scoreType: boolean | FinalImpactScoreType,
) {
  switch (netScoreFormat) {
    case NetScoreFormat.NET_SUM: {
      const formattedNetSum = `${plusIfPositive(value)}${formatImpactScore(value, formatters, scoreType)}`;
      return formattedNetSum.replace('-', '−');
    }
    case NetScoreFormat.NET_RATIO_DECIMAL:
      return (
        <>
          {plusIfPositive(value)}
          {formatWithTwoDecimalsGrayNA(value)}
        </>
      );
    case NetScoreFormat.NET_RATIO:
      // eslint-disable-next-line no-case-declarations
      const formatted = formatNetImpactRatio(value, true);
      if (typeof formatted === 'string') {
        return formatted.replace('-', '−');
      }
      return formatted;
  }
}
